import React from "react";
import config from "../../../config";
import { isFlexiSurveyAssetTypeKey } from "../constants/managedSurveyAssetTypes";
import { convertAssetToPanel } from "../helpers/HvSubstationHelpers";
import { Asset } from "../interfaces/Asset";
import { HvSubstationMetadata } from "../interfaces/HvSubstationMetadata";
import { Mapping } from "../interfaces/Mapping";
import { ExistingSurveyableAsset, Panel } from "../interfaces/SurveyableAsset";
import { SwitchGearSubmission } from "../interfaces/Switchgear";
import { handleResponseArray } from "./APIHandlerHelper";
import { ApiHandlerPureComponent } from "./ApiHandlerPureComponent";

interface Props {
  onAuthError?: () => void;
}

export default class AssetAPIs extends React.PureComponent<Props> {
  private apiResponseHandler = new ApiHandlerPureComponent({
    onAuthError: this.props.onAuthError,
  });

  private static readonly BASE_URL: string = `${config.apiGateway.META_API}/api/Assets/`;

  getAssetById = (assetId: number): Promise<Asset | null> => {
    const apiAddress = `${AssetAPIs.BASE_URL}${assetId}`;
    return this.apiResponseHandler.get(apiAddress).then((data) => {
      if (!data) {
        return null;
      }
      return data;
    });
  };

  getHVSubstationById = (hvSubstationId: number | string): Promise<HvSubstationMetadata> => {
    const apiAddress = `${AssetAPIs.BASE_URL}hvSubstation/${hvSubstationId}`;
    return this.apiResponseHandler.get(apiAddress);
  };

  getHVSubstationsWithCompanyAndSiteByCustomer = (customerId: number): Promise<HvSubstationMetadata[]> => {
    const apiAddress = `${AssetAPIs.BASE_URL}hvSubstations/customer/${customerId}`;

    return this.apiResponseHandler.get(apiAddress).then((data) => {
      if (!data) {
        return [];
      }
      if (!Array.isArray(data)) {
        return [];
      }
      return data;
    });
  };

  getHVSubstationsWithCompanyAndSiteByCustomerAndCompany = (
    customerId: number,
    companyId: number,
  ): Promise<HvSubstationMetadata[]> => {
    const apiAddress = `${AssetAPIs.BASE_URL}hvSubstations/customer/${customerId}/company/${companyId}`;

    return this.apiResponseHandler.get(apiAddress).then((data) => {
      if (!data) {
        return [];
      }
      if (!Array.isArray(data)) {
        return [];
      }
      return data;
    });
  };

  getHVSubstationsWithCompanyAndSiteByCustomerAndSite = async (
    customerId: number,
    siteId: number,
    getAttributes?: boolean,
  ): Promise<HvSubstationMetadata[]> => {
    const apiAddress = `${AssetAPIs.BASE_URL}hvSubstations/customer/${customerId}/site/${siteId}${getAttributes ? "?fetchAttributes=true" : ""}`;

    const data = await this.apiResponseHandler.get(apiAddress);
    if (!data) {
      return [];
    }
    if (!Array.isArray(data)) {
      return [];
    }
    return data;
  };

  getHVSubstationsByCustomer = (customerId: number): Promise<Asset[]> => {
    const apiAddress = `${AssetAPIs.BASE_URL}ByCustomerAndAssetType/customerId/${customerId}/assetType/HVSubstation`;

    return this.apiResponseHandler.get(apiAddress).then((data) => {
      if (!data) {
        return [];
      }
      if (!Array.isArray(data)) {
        return [];
      }
      return data;
    });
  };

  getHVSubstationsByCustomerAndCompany = (customerId: number, companyId: number): Promise<Asset[]> => {
    const apiAddress = `${AssetAPIs.BASE_URL}ByCustomerCompanyAndAssetType/customerId/${customerId}/companyId/${companyId}/assetType/HVSubstation`;

    return this.apiResponseHandler.get(apiAddress).then((data) => {
      if (!data) {
        return [];
      }
      if (!Array.isArray(data)) {
        return [];
      }
      return data;
    });
  };
  getSwitchgearsByHVSubstation = (substationId: number | string): Promise<Asset[]> => {
    const apiAddress = `${AssetAPIs.BASE_URL}children/${substationId}/Assets`;

    return this.apiResponseHandler.get(apiAddress).then((data) => {
      if (!data) {
        return [];
      }
      if (!Array.isArray(data)) {
        return [];
      }
      return data
        .filter((asset) => isFlexiSurveyAssetTypeKey(asset.assetTypeKey))
        .sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  getAssetsByHVSubstation = (substationId: number | string): Promise<Asset[]> => {
    const apiAddress = `${AssetAPIs.BASE_URL}children/${substationId}/Assets`;

    return this.apiResponseHandler.get(apiAddress).then((data) => {
      if (!data) {
        return [];
      }
      if (!Array.isArray(data)) {
        return [];
      }
      return data.sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  getPanelsBySwitchgear = (switchgearId: number): Promise<Panel[]> => {
    const apiAddress = `${AssetAPIs.BASE_URL}children/${switchgearId}/assets`;

    return this.apiResponseHandler.get(apiAddress).then((data) => {
      const dataArray = handleResponseArray<Asset>(data);

      return dataArray
        .filter((panel) => panel.assetTypeKey === "Panel" || panel.assetTypeKey === "Line")
        .map((asset) => convertAssetToPanel(asset));
    });
  };

  async getPanelsBySwitchgears(switchgearIds: number[] | undefined): Promise<Panel[]> {
    if (switchgearIds == null || switchgearIds.length < 1) {
      return [];
    }
    const getAssetPromises = switchgearIds.map((switchgearId) => this.getPanelsBySwitchgear(switchgearId));
    return (await Promise.all(getAssetPromises)).flatMap((panel) => panel);
  }

  updateAsset = async (asset: Asset): Promise<any> => {
    var apiAddress = `${AssetAPIs.BASE_URL}${asset.id}`;
    return this.apiResponseHandler.put(apiAddress, JSON.stringify(asset), false);
  };

  addSwitchgearsToExistingHvSubstation = async (switchgearSubmission: SwitchGearSubmission): Promise<any> => {
    var apiAddress = `${AssetAPIs.BASE_URL}SwitchGear/HVSubstation/${switchgearSubmission.hvSubstationId}`;
    return this.apiResponseHandler.post(apiAddress, JSON.stringify(switchgearSubmission.switchgears), false);
  };

  deleteSwitchgear = async (switchgearAssetId: number): Promise<null> => {
    const apiAddress = `${AssetAPIs.BASE_URL}hvSubstation/switchGear/${switchgearAssetId}`;
    return this.apiResponseHandler.delete(apiAddress, false);
  };

  updateSwitchgear = async (switchgear: ExistingSurveyableAsset): Promise<number> => {
    const apiAddress = `${AssetAPIs.BASE_URL}hvSubstation/switchGear/${switchgear.id}`;
    return this.apiResponseHandler.put(apiAddress, JSON.stringify(switchgear));
  };

  deletePanel = async (panelAssetId: number): Promise<null> => {
    const apiAddress = `${AssetAPIs.BASE_URL}hvSubstation/panel/${panelAssetId}`;
    return this.apiResponseHandler.delete(apiAddress, false);
  };

  getParentMappingByAssetId = async (assetId: number): Promise<Mapping | null> => {
    const apiAddress = `${AssetAPIs.BASE_URL}parentMappings/${assetId}`;
    return this.apiResponseHandler.get(apiAddress).then((data) => {
      if (!data) {
        return null;
      }
      return data;
    });
  };
}
